/* eslint-disable */
import React, { useState } from 'react';
import { authAPI } from '../../services/api';
import PasswordRules from '../common/PasswordRules';
import { validatePassword } from '../../utils/passwordPolicy';

// UX-005: one policy, shared with the server
const PASSWORD_REGEX = { test: (p) => validatePassword(p) === null };

export default function MustChangePassword({ username }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirm, setConfirm]                 = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');
  const [acceptLegal, setAcceptLegal]          = useState(false);

  const passwordStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 8) return { level: 'weak', label: 'Too short', color: '#ef4444' };
    if (!PASSWORD_REGEX.test(pw)) return { level: 'fair', label: 'Fair — add uppercase, number & symbol', color: '#f59e0b' };
    if (pw.length >= 12) return { level: 'strong', label: 'Strong', color: '#10b981' };
    return { level: 'good', label: 'Good', color: '#3b82f6' };
  };
  const strength = passwordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    {
      const pwProblem = validatePassword(newPassword);
      if (pwProblem) return setError(pwProblem);
    }
    if (newPassword !== confirm) return setError('Passwords do not match.');
    if (!acceptLegal) return setError('You must accept the Terms of Service, EULA and Privacy Policy to continue.');
    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      // LEGAL-002: Record acceptance as part of this same first-login setup
      // step, rather than a separate gate straight after this one.
      await authAPI.acceptLegal().catch(() => {}); // non-fatal — the app-level gate will catch it if this somehow fails
      // Reload so the app fetches fresh /me data and the gate clears
      window.location.reload();
    } catch (err) {
      setError(err?.error || 'Failed to change password. Please check your current password and try again.');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '420px' }}>
        <div className="login-card" style={{ width: '100%' }}>
          <div className="login-logo">
            <img src="/casavello-stacked.png" alt="casavello" style={{ width: '140px', height: 'auto', objectFit: 'contain', marginBottom: '0.5rem' }} />
          </div>

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔑</div>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.25rem', fontWeight: 700 }}>Set Your Password</h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {username ? <>Welcome, <strong>{username}</strong>. </> : null}
              Before continuing, please set a new password of your own choosing.
            </p>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Temporary Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="The temporary password you were given"
                required
                autoFocus
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Choose a strong password"
                required
                autoComplete="new-password"
              />
              {strength && (
                <div style={{ marginTop: '0.4rem' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {['weak','fair','good','strong'].map((lvl, i) => (
                      <div key={lvl} style={{
                        flex: 1, height: '4px', borderRadius: '2px',
                        background: ['weak','fair','good','strong'].indexOf(strength.level) >= i ? strength.color : 'var(--border)'
                      }} />
                    ))}
                  </div>
                  <small style={{ color: strength.color, fontSize: '0.75rem' }}>{strength.label}</small>
                </div>
              )}
              <PasswordRules password={newPassword} />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                required
                autoComplete="new-password"
              />
              {confirm && confirm !== newPassword && (
                <small style={{ color: '#ef4444', fontSize: '0.75rem' }}>Passwords don't match</small>
              )}
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '0.85rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
              <p style={{ margin: '0 0 0.5rem', color: 'var(--text-secondary)' }}>Please also review:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.6rem' }}>
                <a href="/terms" onClick={e => { e.preventDefault(); window.open('/terms', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', fontWeight: 600, cursor: 'pointer' }}>📄 Terms of Service ↗</a>
                <a href="/eula" onClick={e => { e.preventDefault(); window.open('/eula', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', fontWeight: 600, cursor: 'pointer' }}>📄 EULA ↗</a>
                <a href="/privacy" onClick={e => { e.preventDefault(); window.open('/privacy', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', fontWeight: 600, cursor: 'pointer' }}>📄 Privacy Policy ↗</a>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acceptLegal}
                  onChange={e => setAcceptLegal(e.target.checked)}
                  style={{ marginTop: '0.15rem', flexShrink: 0 }}
                />
                <span>I have read and agree to the Terms of Service, EULA and Privacy Policy.</span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-success"
              style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginTop: '0.5rem' }}
              disabled={loading || !currentPassword || !PASSWORD_REGEX.test(newPassword) || !confirm || !acceptLegal}
            >
              {loading ? 'Saving...' : '🔐 Set New Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
